import { forwardRef, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getPaymentTermsOptions } from '@/modules/clients/constants/payment-terms';
import { ClientInfoFormData, getClientInfoSchema } from '@/modules/clients/validations';
import { SingleSelect } from '@/modules/common';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { useTeamMemberSelectors } from '@/modules/team';
import { compareFormValues } from '@/utils/form-state.utils';

import { Client, ClientFormData, ClientStatus, PaymentTerms } from '../../../types';

export interface ClientInfoTabProps extends BaseTabProps<Client, ClientFormData> {}

export interface ClientInfoTabRef extends FeatureTabRef<ClientFormData> {}

export const ClientInfoTab = forwardRef<ClientInfoTabRef, ClientInfoTabProps>((props, ref) => {
  const { t } = useTranslation(['authenticated.clients', 'common']);
  const { mode, featureData: clientData, draftData } = props;
  const schema = getClientInfoSchema(t);

  const { loadManagers, searchManagers } = useTeamMemberSelectors();

  const initialValues = useMemo((): ClientInfoFormData => {
    if (mode === FeatureMode.Create) {
      return {
        name: draftData?.name ?? '',
        email: draftData?.email ?? '',
        managerId: draftData?.managerId ?? '',
        paymentTerms: draftData?.paymentTerms ?? PaymentTerms.Net30,
        billingAddress: draftData?.billingAddress ?? '',
        shippingAddress: draftData?.shippingAddress ?? '',
      };
    }

    return {
      name: draftData?.name ?? clientData?.name ?? '',
      email: draftData?.email ?? clientData?.email ?? '',
      managerId: draftData?.managerId ?? clientData?.manager?.id ?? '',
      paymentTerms: draftData?.paymentTerms ?? clientData?.paymentTerms ?? PaymentTerms.Net30,
      billingAddress: draftData?.billingAddress ?? clientData?.billingAddress ?? '',
      shippingAddress: draftData?.shippingAddress ?? clientData?.shippingAddress ?? '',
    };
  }, [mode, clientData, draftData]);

  const form = useForm<ClientInfoFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: initialValues,
  });

  const getFormData = (): ClientFormData => {
    const formValues = form.getValues();

    return {
      name: formValues.name,
      status: clientData?.status || ClientStatus.Active,
      email: formValues.email,
      managerId: formValues.managerId,
      paymentTerms: formValues.paymentTerms,
      billingAddress: formValues.billingAddress,
      shippingAddress: formValues.shippingAddress,
      avatar: formValues.avatar,
    } as ClientFormData;
  };

  // Use the tab form state hook with adapter
  const { isReadMode, handleSubmit } = useTabFormState<Client, ClientInfoFormData>(props, {
    form,
    initialValues,
    isArchivedFn: client => client?.status === ClientStatus.Archived,
    triggerValidationOnMount: true,
    customDirtyStateFn: (currentValues, initialValues) => {
      if (!form.formState.isValid) {
        return false;
      }

      // Check if there are actual changes
      if (initialValues) {
        return compareFormValues(currentValues, initialValues);
      }

      // Fallback to form's built-in dirty state
      return form.formState.isDirty;
    },
  });

  const shouldShowWarningForMemberId = (item: DropdownSearchItem) => {
    return item.isDeleted ? t('details.deletedEntityWanring') : undefined;
  };

  // Include deleted manager in dropdown options if exists
  const additionalManagerItems = useMemo(() => {
    if (clientData?.manager?.isDeleted) {
      return [
        {
          id: clientData.manager.id,
          name: clientData.manager.name,
          secondaryText: clientData.manager.email ?? undefined,
          avatarLink: clientData.manager.avatar ?? undefined,
          isDeleted: true,
        },
      ];
    }
    return undefined;
  }, [clientData?.manager]);

  // Create initial selected item for manager to display immediately without fetching
  const initialManagerItem = useMemo(() => {
    if (clientData?.manager) {
      return {
        id: clientData.manager.id,
        name: clientData.manager.name,
        secondaryText: clientData.manager.email ?? undefined,
        avatarLink: clientData.manager.avatar ?? undefined,
        isDeleted: clientData.manager.isDeleted,
      };
    }
    return undefined;
  }, [clientData?.manager]);

  const tabContent = (
    <Form {...form}>
      <form className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('details.tabs.info.mainInfoTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('details.tabs.info.fullName')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('details.tabs.info.namePlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('details.tabs.info.email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t('details.tabs.info.emailPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === FeatureMode.Create && (
              <FileUploadField
                name="avatar"
                control={form.control}
                label={t('details.tabs.info.avatar')}
                disabled={isReadMode}
              />
            )}
            <SingleSelectWithPagination
              name="managerId"
              initialSelectedItem={initialManagerItem}
              showWarning={shouldShowWarningForMemberId}
              additionalItems={additionalManagerItems}
              control={form.control}
              label={t('details.tabs.info.manager')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.managerPlaceholder')
              }
              disabled={isReadMode}
              showAvatars={true}
              showDescriptionInOptions={true}
              showDescriptionInSelector={false}
              avatarShape="circle"
              queryKey={['team-members-short', 'managers']}
              loadItems={loadManagers}
              searchItems={searchManagers}
              isClearSelectionEnabled={true}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="mr-4">{t('details.tabs.info.mainBillingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SingleSelect
              name="paymentTerms"
              control={form.control}
              options={getPaymentTermsOptions(t)}
              label={t('details.tabs.info.paymentTerms')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.paymentTermsPlaceholder')
              }
              emptyMessage={t('common:loading')}
              disabled={isReadMode}
              searchable={false}
            />
            <FormField
              control={form.control}
              name="billingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('details.tabs.info.billingAddress')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder={t('details.tabs.info.billingAddressPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shippingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('details.tabs.info.shippingAddress')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder={t('details.tabs.info.shippingAddressPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      </form>
    </Form>
  );

  return (
    <BaseFeatureTab
      {...props}
      ref={ref}
      onSubmit={handleSubmit}
      isValid={form.formState.isValid}
      getFormData={getFormData}
      isArchivedFn={data => data?.status === ClientStatus.Archived}
    >
      {tabContent}
    </BaseFeatureTab>
  );
});
